import {
    Box,
    Button,
    IconButton,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { httpClient } from '../../../common/httpclient';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import { v4 as uuidv4 } from 'uuid';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useRecoilValue } from 'recoil';
import {
    ArrowLeft,
    Copy,
    GripVertical,
    Info,
    Pencil,
    Plus,
    Trash2,
} from 'lucide-react';
import { sidebarExpandedSelector } from '../../../component/layout/sidebarState';
import { useAppBarTitle } from '../../../component/layout/AppBarActions';
import { MobileGroupForm } from '../../../component/checklist/MobileChecklist';
import useIsMobileShell from '../../../hooks/useIsMobileShell';

// ============================================================================
// STANDALONE GROUP EDITOR (item 130, "Grupo de inspeção — editor") — página
// cheia usada pelas rotas `/checklist/:id/group`, `/checklist/:id/group/new`,
// `/checklist/:id/group/edt` e `/checklist/group/add`/`/checklist/group/edt`.
// DESIGN-SYSTEM.md §5.15 (drag-and-drop nativo) + §5.17 (nova extensão desta
// tarefa) + `prototipo-checklist-grupo-editor.html`: `form-hero` (voltar +
// título + anel de progresso por 2 critérios) → `inline-note` de escopo →
// card "Dados do grupo" → card "Itens de inspeção do grupo" (lista
// drag-and-drop nativa com mini-formulário inline de edição reaproveitando o
// componente `check-answers`) → preview sticky reaproveitando
// `check-group`/`check-item`/`check-answers` em modo somente-leitura.
//
// Resolução de `groupId`/`tplId` (3 modos da tarefa + 1 modo avulso-novo já
// coberto por `/checklist/group/add`): como as 4 rotas registradas coexistem
// com a app real (diferente do protótipo estático, que só tem 1 arquivo +
// query string), o `:id` de path de cada rota é mapeado para o papel mais
// natural dela — `/group/new` e `/group/edt` tratam `:id` como o `tplId`
// (mesma convenção de `/checklist/:id/edt`, o editor de template irmão);
// `/group` (sem sufixo) trata `:id` como o próprio `groupId` (edição avulsa
// via path); `?groupId=`/`?tplId=` na query string sempre têm prioridade
// sobre o `:id` do path quando presentes, para bater 1:1 com a convenção de
// query string descrita pela tarefa.
// ============================================================================

// Conjunto de respostas fixo (OK/NOK/Manutenção) — mesma normalização usada
// por `StatusChip domain="checklist"` (`component/common/StatusChip.js`) e
// pelo fallback já hard-coded em `Filler.js`/`ChecklistFillerModal.js`
// (`feedback_id: 'ok'|'nok'|'manutencao'`). O backend (`checklist_evaluation_
// type_answers`) não amarra "quais respostas um item aceita" a um item
// específico — isso é global por grupo — então os toggles por item deste
// editor são uma aproximação apenas de configuração/estrutura (ver nota mais
// abaixo em `buildFeedbackPayload`/no relatório da tarefa).
const ANSWER_KINDS = ['ok', 'nok', 'manut'];
const ANSWER_META = {
    ok: { label: 'OK', feedbackName: 'OK', description: 'Item em condições adequadas' },
    nok: { label: 'NOK', feedbackName: 'NOK', description: 'Item fora de conformidade' },
    manut: { label: 'Manutenção', feedbackName: 'Manutenção', description: 'Item necessita de manutenção' },
};
// Nomes alternativos aceitos ao tentar casar um `group_feedback` já existente
// (grupos antigos, criados pelo formulário legado, usavam "Atenção" em vez de
// "NOK" como sugestão padrão) — evita gerar um novo `feedback_id` toda vez
// que um grupo antigo é salvo por aqui, o que órfã(zaria) o `answer_id` já
// referenciado por avaliações de checklist antigas.
const ANSWER_ALIASES = {
    ok: ['ok'],
    nok: ['nok', 'atencao'],
    manut: ['manutencao'],
};

function normalizeAnswerName(value) {
    return String(value || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase();
}

function answerTone(theme, kind) {
    switch (kind) {
        case 'ok':
            return { bg: theme.palette.success.bg, fg: theme.palette.success.main };
        case 'nok':
            return { bg: theme.palette.error.bg, fg: theme.palette.error.main };
        case 'manut':
            return { bg: theme.palette.warning.bg, fg: theme.palette.warning.main };
        default:
            return { bg: theme.palette.background.subtle, fg: theme.palette.text.disabled };
    }
}

// Casca visual "ff-input"/"ff-textarea" — cópia 1:1 de `standaloneFieldSx` já
// usada em `Form.js` (`StandaloneChecklistForm`)/`categories/Form.js`/
// `suppliers/Form.js`/etc.
function useGroupStandaloneFieldSx() {
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
            '&.MuiInputBase-multiline': {
                height: 'auto',
                minHeight: '96px',
                alignItems: 'flex-start',
                padding: '24px 14px 7px 14px',
            },
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
        '& .MuiInputBase-multiline textarea': {
            resize: 'vertical',
        },
    };

    return {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };
}

function parseGroupApiError(data) {
    if (data == null) return 'Erro desconhecido';
    if (typeof data === 'string') return data;
    if (typeof data === 'object') {
        return data.message || data.error || data.id || 'Erro ao processar a solicitação';
    }
    return String(data);
}

// Botão de resposta (`check-answers`) — reaproveita a mesma paleta de tons
// (success/error/warning) de `StatusChip domain="checklist"`. Sem `onClick`
// vira um chip somente-leitura (linha da lista / preview estrutural); com
// `onClick`, vira o toggle interativo do mini-formulário de edição.
function AnswerToggleButton({ kind, selected, disabled, onClick, dense }) {
    const theme = useTheme();
    const { bg, fg } = answerTone(theme, kind);
    const interactive = typeof onClick === 'function';
    return (
        <Box
            component="button"
            type="button"
            onClick={interactive ? onClick : undefined}
            disabled={interactive ? disabled : true}
            sx={{
                fontFamily: 'inherit',
                border: '1.5px solid',
                borderColor: selected ? fg : 'divider',
                backgroundColor: selected ? bg : 'background.paper',
                color: selected ? fg : 'text.secondary',
                borderRadius: '20px',
                fontSize: dense ? 10 : 11,
                fontWeight: 700,
                lineHeight: 1,
                padding: dense ? '3px 9px' : '6px 12px',
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                cursor: interactive ? 'pointer' : 'default',
                transition: 'background-color .12s ease, border-color .12s ease, color .12s ease',
                '&:disabled': {
                    opacity: interactive ? 0.35 : 1,
                    cursor: interactive ? 'not-allowed' : 'default',
                },
            }}
        >
            {ANSWER_META[kind].label}
        </Box>
    );
}

function DefaultAnswerBadge({ kind }) {
    const theme = useTheme();
    const { bg, fg } = answerTone(theme, kind);
    return (
        <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', padding: '3px 9px', borderRadius: '20px',
            fontSize: 10, fontWeight: 700, lineHeight: 1, backgroundColor: bg, color: fg, whiteSpace: 'nowrap',
        }}>
            Padrão: {ANSWER_META[kind].label}
        </Box>
    );
}

// Chips "mini" da linha da lista — todas as respostas habilitadas aparecem
// preenchidas (mesmo `.check-answers.mini button.sel` do protótipo); a
// resposta padrão (se houver) é indicada por um badge textual separado, não
// por uma segunda variação de cor entre os próprios chips.
function AnswerChipsRow({ options }) {
    const enabled = ANSWER_KINDS.filter((k) => options[k]);
    if (enabled.length === 0) {
        return (
            <Typography component="span" sx={{ fontSize: 10.5, color: 'text.disabled', fontStyle: 'italic' }}>
                Nenhuma resposta habilitada
            </Typography>
        );
    }
    return enabled.map((k) => <AnswerToggleButton key={k} kind={k} selected dense />);
}

// Chips da pré-visualização estrutural — todas as respostas habilitadas
// aparecem, mas só a resposta padrão vem preenchida (mesmo
// `.check-answers.preview` do protótipo: `sel` só na resposta `def`).
function AnswerChipsPreview({ options, def }) {
    const enabled = ANSWER_KINDS.filter((k) => options[k]);
    if (enabled.length === 0) {
        return (
            <Typography component="span" sx={{ fontSize: 10.5, color: 'text.disabled', fontStyle: 'italic' }}>
                —
            </Typography>
        );
    }
    return enabled.map((k) => <AnswerToggleButton key={k} kind={k} selected={k === def} />);
}

const rowActionIconSx = {
    width: 28, height: 28, border: '1px solid', borderColor: 'divider', borderRadius: '8px',
    color: 'text.disabled', '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
};

// Linha somente-leitura de um item (alça de arraste + ordem + texto + chips +
// ações Duplicar/Editar/Remover).
function ItemRow({ item, index, dragLocked, dragging, onDragStart, onDragOver, onDragEnd, onDuplicate, onEdit, onRemove }) {
    return (
        <Box
            draggable={!dragLocked}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            sx={{
                display: 'flex', alignItems: 'center', gap: '12px', p: '12px 14px',
                border: '1px solid', borderColor: 'divider', borderRadius: '12px', backgroundColor: 'background.paper',
                opacity: dragging ? 0.35 : 1,
                cursor: dragLocked ? 'default' : 'grab',
            }}
        >
            <Box sx={{ color: 'text.disabled', opacity: dragLocked ? 0.35 : 1, display: 'flex', flexShrink: 0, cursor: dragLocked ? 'not-allowed' : 'grab' }} title="Arraste para reordenar">
                <GripVertical size={16} />
            </Box>
            <Box sx={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '7px',
                backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider', fontSize: 11, fontWeight: 800, color: 'text.secondary', flexShrink: 0,
            }}>
                {index + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                    fontSize: 13, fontWeight: 700, color: item.insp_item_name ? 'text.primary' : 'text.disabled',
                    fontStyle: item.insp_item_name ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {item.insp_item_name || '(sem texto)'}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: '6px', rowGap: '4px' }}>
                    <AnswerChipsRow options={item.options} />
                    {item.def ? <DefaultAnswerBadge kind={item.def} /> : (
                        <Typography component="span" sx={{ fontSize: 10.5, color: 'text.disabled', fontStyle: 'italic' }}>
                            Sem resposta padrão
                        </Typography>
                    )}
                </Stack>
            </Box>
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                <IconButton size="small" title="Duplicar item" onClick={onDuplicate} sx={rowActionIconSx}>
                    <Copy size={14} strokeWidth={2} />
                </IconButton>
                <IconButton size="small" title="Editar item" onClick={onEdit} sx={rowActionIconSx}>
                    <Pencil size={14} strokeWidth={2} />
                </IconButton>
                <IconButton
                    size="small"
                    title="Remover item"
                    onClick={onRemove}
                    sx={{ ...rowActionIconSx, '&:hover': { color: 'error.main', borderColor: 'error.main', backgroundColor: 'error.bg' } }}
                >
                    <Trash2 size={14} strokeWidth={2} />
                </IconButton>
            </Stack>
        </Box>
    );
}

// Mini-formulário inline de edição de item — texto + toggles "Respostas
// aceitas" + seletor de "Resposta padrão" (restrito às respostas habilitadas)
// — reaproveita 1:1 o componente visual `check-answers` da aba Checklist de
// `service_order/Operator.js`, aqui em modo interativo de configuração.
function ItemEditPanel({ item, index, onChangeText, onToggleOption, onSetDefault, onCancel, onCommit }) {
    const inputRef = useRef(null);
    const standaloneFieldSx = useGroupStandaloneFieldSx();
    useEffect(() => {
        const t = setTimeout(() => inputRef.current?.focus(), 30);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <Box sx={{
            border: '1.5px solid', borderColor: 'primary.main', borderRadius: '12px', p: '16px',
            backgroundColor: 'background.subtle', boxShadow: (t) => `0 0 0 3px ${t.palette.info.bg}`,
        }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                <Box sx={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '7px',
                    backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', fontSize: 11, fontWeight: 800, color: 'text.secondary',
                }}>
                    {index + 1}
                </Box>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                    Editando item
                </Typography>
            </Stack>

            <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                label={<>Texto do item <Box component="span" sx={{ color: 'error.main' }}>*</Box></>}
                value={item.insp_item_name}
                onChange={(e) => onChangeText(e.target.value)}
                variant="outlined"
                // Casca `ff-input`; fundo `paper` (o painel de edição já é `subtle`).
                sx={{
                    mb: 2,
                    ...standaloneFieldSx,
                    '& .MuiOutlinedInput-root': {
                        ...standaloneFieldSx['& .MuiOutlinedInput-root'],
                        backgroundColor: 'background.paper',
                    },
                }}
            />

            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: 1 }}>
                Respostas aceitas
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 2 }}>
                {ANSWER_KINDS.map((k) => (
                    <AnswerToggleButton key={k} kind={k} selected={item.options[k]} onClick={() => onToggleOption(k)} />
                ))}
            </Stack>

            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: 1 }}>
                Resposta padrão (opcional)
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 2.5 }}>
                {ANSWER_KINDS.map((k) => (
                    <AnswerToggleButton key={k} kind={k} selected={item.def === k} disabled={!item.options[k]} onClick={() => onSetDefault(k)} />
                ))}
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" variant="outlined" onClick={onCancel}>Cancelar</Button>
                <Button size="small" variant="contained" onClick={onCommit}>Concluir edição</Button>
            </Stack>
        </Box>
    );
}

// Estrutura `check-group`/`check-item` somente-leitura (preview sticky) —
// mesmo cabeçalho em gradiente da aba Checklist de `service_order/Operator.js`
// (`theme.palette.gradient.brand`), aqui mostrando a estrutura completa do
// grupo (todos os itens + chips de resposta), não apenas nome+contagem.
function PreviewCheckGroup({ name, items }) {
    const theme = useTheme();
    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflow: 'hidden', pointerEvents: 'none' }}>
            <Box sx={{
                p: '10px 13px', background: theme.palette.gradient.brand, color: '#fff', fontSize: 12, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
            }}>
                <span>{name}</span>
                <Box component="span" sx={{ fontSize: '10.5px', fontWeight: 700, bgcolor: 'rgba(255,255,255,.2)', px: '8px', py: '3px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                    {items.length} ite{items.length === 1 ? 'm' : 'ns'}
                </Box>
            </Box>
            {items.length === 0 ? (
                <Typography sx={{ fontSize: 11, color: 'text.disabled', p: '14px 13px' }}>
                    Nenhum item adicionado ainda — a pré-visualização aparecerá aqui conforme você adicionar itens ao lado.
                </Typography>
            ) : items.map((it, idx) => (
                <Box
                    key={it.insp_item_id}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: '10px', p: '10px 13px', flexWrap: 'wrap',
                        borderBottom: idx < items.length - 1 ? '1px solid' : 'none', borderColor: 'divider',
                    }}
                >
                    <Typography sx={{ flex: 1, minWidth: 120, fontSize: 12, fontWeight: 600, color: 'text.primary' }}>
                        {it.insp_item_name || '(sem texto)'}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        <AnswerChipsPreview options={it.options} def={it.def} />
                    </Stack>
                </Box>
            ))}
        </Box>
    );
}

function StandaloneGroupForm() {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { id: pathId } = useParams();
    const [searchParams] = useSearchParams();
    const standaloneFieldSx = useGroupStandaloneFieldSx();
    const isMobileShell = useIsMobileShell();
    useAppBarTitle(isMobileShell ? 'Grupo de inspeção' : '');

    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

    // Resolução de groupId/tplId — ver nota de decisão logo acima do bloco
    // "STANDALONE GROUP EDITOR".
    const pathname = location.pathname;
    let resolvedGroupId = searchParams.get('groupId') || null;
    let resolvedTplId = searchParams.get('tplId') || null;
    if (pathname.endsWith('/group/new')) {
        resolvedTplId = resolvedTplId || pathId || null;
    } else if (pathname.endsWith('/group/edt')) {
        resolvedTplId = resolvedTplId || pathId || null;
    } else if (pathname.endsWith('/group') && pathId) {
        resolvedGroupId = resolvedGroupId || pathId;
    }
    const groupId = resolvedGroupId;
    const tplId = resolvedTplId;
    const isNew = !groupId;
    const backTarget = tplId ? `/checklist/${tplId}/edt` : '/checklist/groups';

    const [loadingData, setLoadingData] = useState(true);
    const [saving, setSaving] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');

    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const [description, setDescription] = useState('');
    const [existingFeedback, setExistingFeedback] = useState([]);
    const [templateName, setTemplateName] = useState('');

    // Itens do grupo — shape alinhado ao contrato real de `fv-api`
    // (`ChecklistInspectionItem`: insp_item_id/insp_item_name/insp_item_order/
    // active), com `options`/`def` como campos EXTRAS, só de UI: o schema de
    // `checklist_inspection_items` não tem coluna para "quais respostas este
    // item aceita" nem "resposta padrão" (ver migração
    // `20250215111247_checklist_vehicle.sql`) — isso é aproximado aqui como
    // metadado de estrutura/edição, não persiste no backend, e por isso
    // sempre volta para "todas habilitadas, sem padrão" quando o grupo é
    // recarregado (ver relatório da tarefa).
    const [items, setItems] = useState([]);

    const [editingItemId, setEditingItemId] = useState(null);
    const [editingIsNew, setEditingIsNew] = useState(false);
    const [editingBackup, setEditingBackup] = useState(null);
    const [draggingId, setDraggingId] = useState(null);
    const dragIdRef = useRef(null);

    const showToast = (msg, sev = 'warning') => {
        setSeverity(sev);
        setMessage(msg);
        setOpenSnackbar(true);
    };

    useEffect(() => {
        let active = true;
        const load = async () => {
            setLoadingData(true);
            try {
                const requests = [];
                if (groupId) requests.push(httpClient.get(`/checklist/group/${groupId}`));
                if (tplId) requests.push(httpClient.get(`/checklist/${tplId}`));
                const results = await Promise.allSettled(requests);
                if (!active) return;

                let idx = 0;
                if (groupId) {
                    const res = results[idx++];
                    if (res.status === 'fulfilled') {
                        const data = res.value.data || {};
                        setName(data.group_name || '');
                        setDescription(data.group_description || '');
                        setExistingFeedback(Array.isArray(data.group_feedback) ? data.group_feedback : []);
                        const rawItems = Array.isArray(data.group_inspection_items) ? data.group_inspection_items : [];
                        setItems(
                            rawItems
                                .filter((it) => it.active !== false)
                                .sort((a, b) => (a.insp_item_order || 0) - (b.insp_item_order || 0))
                                .map((it) => ({
                                    insp_item_id: it.insp_item_id,
                                    insp_item_name: it.insp_item_name || '',
                                    active: true,
                                    options: { ok: true, nok: true, manut: true },
                                    def: null,
                                }))
                        );
                    } else {
                        console.error('Erro ao carregar grupo de inspeção:', res.reason);
                        showToast('Erro ao carregar dados do grupo de inspeção.', 'error');
                    }
                }
                if (tplId) {
                    const res = results[idx++];
                    if (res.status === 'fulfilled') {
                        setTemplateName(res.value.data?.name || '');
                    }
                }
            } finally {
                if (active) setLoadingData(false);
            }
        };
        load();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId, tplId]);

    // ===== Anel de progresso — 2 critérios: nome preenchido, ao menos 1 item =====
    const RING_R = 23;
    const RING_CIRC = 2 * Math.PI * RING_R;
    const progressChecks = [Boolean(name.trim()), items.length > 0];
    const progressPct = Math.round((progressChecks.filter(Boolean).length / progressChecks.length) * 100);
    const ringOffset = RING_CIRC - (RING_CIRC * progressPct / 100);

    // ===== CRUD de itens (mesmo comportamento de `prototipo-checklist-grupo-editor.html`) =====
    const nextTempIdRef = useRef(1);

    const addItem = () => {
        if (editingItemId != null) {
            showToast('Conclua a edição do item em aberto antes de adicionar outro.');
            return;
        }
        const newId = `novo-${nextTempIdRef.current++}`;
        const newItem = { insp_item_id: newId, insp_item_name: '', active: true, options: { ok: true, nok: true, manut: true }, def: null };
        setItems((prev) => [...prev, newItem]);
        setEditingItemId(newId);
        setEditingIsNew(true);
        setEditingBackup(null);
    };

    const startEditItem = (itemId) => {
        if (editingItemId != null && editingItemId !== itemId) {
            showToast('Conclua a edição do item em aberto antes de editar outro.');
            return;
        }
        const it = items.find((i) => i.insp_item_id === itemId);
        if (!it) return;
        setEditingItemId(itemId);
        setEditingIsNew(false);
        setEditingBackup(JSON.parse(JSON.stringify(it)));
    };

    const handleChangeItemText = (itemId, value) => {
        setItems((prev) => prev.map((i) => (i.insp_item_id === itemId ? { ...i, insp_item_name: value } : i)));
    };

    const toggleOption = (itemId, key) => {
        setItems((prev) => prev.map((i) => {
            if (i.insp_item_id !== itemId) return i;
            const enabledCount = ANSWER_KINDS.filter((k) => i.options[k]).length;
            if (i.options[key] && enabledCount === 1) {
                showToast('Pelo menos uma resposta precisa ficar disponível para este item.');
                return i;
            }
            const nextOptions = { ...i.options, [key]: !i.options[key] };
            const nextDef = (!nextOptions[key] && i.def === key) ? null : i.def;
            return { ...i, options: nextOptions, def: nextDef };
        }));
    };

    const setItemDefault = (itemId, key) => {
        setItems((prev) => prev.map((i) => {
            if (i.insp_item_id !== itemId || !i.options[key]) return i;
            return { ...i, def: i.def === key ? null : key };
        }));
    };

    const commitEditItem = (itemId) => {
        const it = items.find((i) => i.insp_item_id === itemId);
        const text = (it?.insp_item_name || '').trim();
        if (!text) {
            showToast('Informe o texto do item antes de concluir a edição.');
            return;
        }
        setItems((prev) => prev.map((i) => (i.insp_item_id === itemId ? { ...i, insp_item_name: text } : i)));
        setEditingItemId(null);
        setEditingIsNew(false);
        setEditingBackup(null);
    };

    const cancelEditItem = (itemId) => {
        if (editingIsNew) {
            setItems((prev) => prev.filter((i) => i.insp_item_id !== itemId));
        } else if (editingBackup) {
            setItems((prev) => prev.map((i) => (i.insp_item_id === itemId ? editingBackup : i)));
        }
        setEditingItemId(null);
        setEditingIsNew(false);
        setEditingBackup(null);
    };

    const duplicateItem = (itemId) => {
        if (editingItemId != null) {
            showToast('Conclua a edição do item em aberto antes de duplicar.');
            return;
        }
        setItems((prev) => {
            const idx = prev.findIndex((i) => i.insp_item_id === itemId);
            if (idx === -1) return prev;
            const clone = {
                ...JSON.parse(JSON.stringify(prev[idx])),
                insp_item_id: `novo-${nextTempIdRef.current++}`,
                insp_item_name: `${prev[idx].insp_item_name} (cópia)`,
            };
            const next = [...prev];
            next.splice(idx + 1, 0, clone);
            return next;
        });
        showToast('Item duplicado.', 'success');
    };

    const removeItem = (itemId) => {
        setItems((prev) => prev.filter((i) => i.insp_item_id !== itemId));
        if (editingItemId === itemId) {
            setEditingItemId(null);
            setEditingIsNew(false);
            setEditingBackup(null);
        }
        showToast('Item removido do grupo.', 'success');
    };

    // Shell mobile (`checklists/grupo-form.html`): a folha de item adiciona/edita o texto de uma vez
    // e reordena por "Mover" (sem arrastar), reaproveitando duplicar/remover acima.
    const addItemWithText = (text) => {
        const newId = `novo-${nextTempIdRef.current++}`;
        setItems((prev) => [...prev, { insp_item_id: newId, insp_item_name: text, active: true, options: { ok: true, nok: true, manut: true }, def: null }]);
    };

    const moveItem = (itemId, delta) => {
        setItems((prev) => {
            const fromIdx = prev.findIndex((i) => i.insp_item_id === itemId);
            const toIdx = fromIdx + delta;
            if (fromIdx === -1 || toIdx < 0 || toIdx >= prev.length) return prev;
            const next = [...prev];
            const [moved] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, moved);
            return next;
        });
    };

    // Drag-and-drop nativo HTML5 (sem biblioteca, DESIGN-SYSTEM.md §5.15) —
    // desabilitado enquanto um item está em edição.
    const handleRowDragStart = (e, itemId) => {
        if (editingItemId != null) { e.preventDefault(); return; }
        dragIdRef.current = itemId;
        setDraggingId(itemId);
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', String(itemId)); } catch (err) { /* noop */ }
    };
    const handleRowDragOver = (e, overItemId) => {
        if (editingItemId != null) return;
        e.preventDefault();
        const draggedId = dragIdRef.current;
        if (draggedId == null || draggedId === overItemId) return;
        setItems((prev) => {
            const fromIdx = prev.findIndex((i) => i.insp_item_id === draggedId);
            const toIdx = prev.findIndex((i) => i.insp_item_id === overItemId);
            if (fromIdx === -1 || toIdx === -1) return prev;
            const next = [...prev];
            const [moved] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, moved);
            return next;
        });
    };
    const handleRowDragEnd = () => {
        dragIdRef.current = null;
        setDraggingId(null);
    };

    // ===== Salvar =====
    // Sempre garante exatamente as 3 respostas fixas (OK/NOK/Manutenção) no
    // `group_feedback` do grupo — preservando o `feedback_id` já existente
    // quando o nome bate (inclusive o alias "Atenção" usado pelo formulário
    // legado), para não invalidar avaliações antigas que já referenciam esse
    // `answer_id`. Novas respostas ganham um uuid gerado no cliente, mesmo
    // padrão já usado pelo antigo formulário embutido (removido) — `UpdateGroup`
    // (`fv-api/checklist/repository.go`) faz upsert por id enviado, não gera
    // id no servidor para feedback/item.
    const buildFeedbackPayload = () => ANSWER_KINDS.map((kind, idx) => {
        const meta = ANSWER_META[kind];
        const aliases = ANSWER_ALIASES[kind];
        const existing = existingFeedback.find((f) => aliases.includes(normalizeAnswerName(f.feedback_name)));
        return {
            feedback_id: existing?.feedback_id || uuidv4(),
            feedback_name: meta.feedbackName,
            feedback_order: idx + 1,
            feedback_description: meta.description,
            active: true,
        };
    });

    const handleSave = async () => {
        if (editingItemId != null) {
            showToast('Conclua a edição do item em aberto antes de salvar o grupo.');
            return;
        }
        const trimmedName = name.trim();
        if (!trimmedName) {
            setNameError('Informe o nome do grupo.');
            return;
        }
        setNameError('');
        setSaving(true);
        try {
            let targetGroupId = groupId;
            // `POST /checklist/group` (`CreatorGroup.Create` →
            // `SaveEvaluationType`) só grava name/description/enterprise_id —
            // o handler recebe `group_feedback`/`group_items` no payload mas o
            // repositório (`checklist/repository.go`) não os persiste na
            // criação (só a linha em `checklist_evaluation_types`). Mesma
            // situação documentada em `StandaloneChecklistForm`/`POST
            // /checklist` (item 129): um segundo request, `PUT
            // /checklist/group/{novoId}` (que já faz upsert de feedback/itens),
            // é necessário logo em seguida — orquestrando dois endpoints já
            // existentes, sem exigir mudança de contrato no backend.
            if (isNew) {
                const createRes = await httpClient.post('/checklist/group', {
                    name: trimmedName,
                    description: description.trim(),
                });
                if (!(createRes.status === 200 || createRes.status === 201)) {
                    showToast(parseGroupApiError(createRes.data) || 'Erro ao criar grupo de inspeção', 'error');
                    return;
                }
                targetGroupId = createRes.data?.id;
            }
            if (!targetGroupId) {
                showToast('Não foi possível determinar o grupo salvo.', 'error');
                return;
            }

            const feedbackPayload = buildFeedbackPayload();
            const itemsPayload = items.map((it, idx) => ({
                insp_item_id: it.insp_item_id.startsWith('novo-') ? uuidv4() : it.insp_item_id,
                insp_item_name: it.insp_item_name.trim(),
                insp_item_order: idx + 1,
                active: true,
                group_id: targetGroupId,
            }));

            const updateRes = await httpClient.put(`/checklist/group/${targetGroupId}`, {
                group_name: trimmedName,
                group_description: description.trim(),
                group_feedback: feedbackPayload,
                group_items: itemsPayload,
            });
            if (!(updateRes.status === 200 || updateRes.status === 204)) {
                showToast(parseGroupApiError(updateRes.data) || 'Erro ao salvar itens do grupo', 'error');
                return;
            }

            setSeverity('success');
            setMessage(
                items.length === 0
                    ? `Grupo "${trimmedName}" salvo sem itens — adicione itens quando quiser.`
                    : (isNew ? `Grupo "${trimmedName}" criado com sucesso!` : `Grupo "${trimmedName}" atualizado com sucesso!`)
            );
            setOpenSnackbar(true);
            const target = (tplId && isNew) ? `${backTarget}?attachGroupId=${targetGroupId}` : backTarget;
            setTimeout(() => navigate(target), 1200);
        } catch (error) {
            console.error('Erro ao salvar grupo de inspeção:', error);
            showToast(parseGroupApiError(error.response?.data) || error.message || 'Erro ao salvar grupo de inspeção', 'error');
        } finally {
            setSaving(false);
        }
    };

    const cardSx = {
        border: '1px solid', borderColor: 'divider', borderRadius: `${theme.customRadius.card}px`,
        boxShadow: theme.customShadows.sm, backgroundColor: 'background.paper', p: '20px 22px', mb: '20px',
    };

    const withDefaultCount = items.filter((i) => i.def).length;
    const groupNameForPreview = name.trim() || 'Novo grupo de inspeção';

    const breadcrumbs = tplId
        ? [
            { title: 'Templates de Checklist', link: '/vehicle_checklist' },
            { title: templateName || 'Template', link: `/checklist/${tplId}/edt` },
        ]
        : [{ title: 'Grupos de Inspeção', link: '/checklist/groups' }];

    if (isMobileShell) {
        return (
            <PageScaffold
                titlePageTarget={isNew ? 'Novo' : 'Editar'}
                links={breadcrumbs}
                message={message}
                severity={severity}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
            >
                {loadingData ? (
                    <Stack spacing={1.75}>
                        <Skeleton variant="rounded" height={76} sx={{ borderRadius: '14px' }} />
                        <Skeleton variant="rounded" height={180} sx={{ borderRadius: '14px' }} />
                    </Stack>
                ) : (
                    <MobileGroupForm
                        name={name}
                        onNameChange={(value) => { setName(value); if (nameError) setNameError(''); }}
                        nameError={nameError}
                        description={description}
                        onDescriptionChange={setDescription}
                        items={items}
                        onAddItem={addItemWithText}
                        onChangeItem={handleChangeItemText}
                        onDuplicateItem={duplicateItem}
                        onMoveItem={moveItem}
                        onRemoveItem={removeItem}
                        onCancel={() => navigate(backTarget)}
                        onSave={handleSave}
                        saving={saving}
                    />
                )}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title={isNew ? 'Novo Grupo de Inspeção' : 'Editar Grupo de Inspeção'}
            titlePageTarget={isNew ? 'Novo' : 'Editar'}
            links={breadcrumbs}
            message={message}
            severity={severity}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* form-hero — voltar + título + anel de progresso. */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                        <IconButton
                            onClick={() => navigate(backTarget)}
                            title="Voltar"
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', flexShrink: 0 }}
                        >
                            <ArrowLeft size={18} strokeWidth={2} />
                        </IconButton>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }} noWrap>
                                {isNew ? 'Novo grupo de inspeção' : `Editar grupo de inspeção — ${name || ''}`}
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                                {isNew
                                    ? 'Defina o nome do grupo e monte a lista de itens de inspeção que ele contém.'
                                    : `Atualize os itens de inspeção de "${name}" e a configuração de respostas de cada um.`}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                        <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                            <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                <circle
                                    cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" strokeLinecap="round"
                                    stroke={theme.palette.primary.main}
                                    strokeDasharray={RING_CIRC}
                                    strokeDashoffset={ringOffset}
                                    style={{ transition: 'stroke-dashoffset .35s ease' }}
                                />
                            </svg>
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
                                {progressPct}%
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Cadastro</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>completo conforme você preenche</Typography>
                        </Box>
                    </Box>
                </Box>

                {/* inline-note — escopo desta tela (não é preenchimento real, item 133/Filler.js). */}
                <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', mb: 2.5, p: '12px 14px',
                    borderRadius: '12px', backgroundColor: 'info.bg',
                }}>
                    <Info size={16} strokeWidth={2} color={theme.palette.primary.main} style={{ marginTop: 1, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12, color: 'text.primary', lineHeight: 1.5 }}>
                        Esta tela só <Box component="b" sx={{ fontWeight: 700 }}>configura a estrutura</Box> do grupo (itens de inspeção + quais respostas cada um aceita). Ela não preenche um checklist de verdade — isso acontece depois, numa ordem de serviço ou no preenchimento avulso (mesmo componente de resposta reaproveitado, só que somente leitura/configuração aqui).
                    </Typography>
                </Box>

                {loadingData ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
                        <Stack spacing={1.75}>
                            <Skeleton variant="rounded" height={160} sx={{ borderRadius: '14px' }} />
                            <Skeleton variant="rounded" height={280} sx={{ borderRadius: '14px' }} />
                        </Stack>
                        <Skeleton variant="rounded" height={340} sx={{ borderRadius: '18px' }} />
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                        <Box>
                            {/* CARD 1 — Dados do grupo. */}
                            <Box sx={cardSx}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 800, mb: '3px' }}>Dados do grupo</Typography>
                                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', maxWidth: 520, lineHeight: 1.5 }}>
                                        Grupos de inspeção são reutilizáveis: o mesmo grupo pode estar anexado a vários templates de checklist ao mesmo tempo. Alterações salvas aqui afetam todos eles.
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <TextField
                                        autoFocus={isNew}
                                        fullWidth
                                        label={<>Nome do grupo <Box component="span" sx={{ color: 'error.main' }}>*</Box></>}
                                        value={name}
                                        onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
                                        error={Boolean(nameError)}
                                        helperText={nameError}
                                        variant="outlined"
                                        inputProps={{ maxLength: 255 }}
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                                <Box>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        label="Descrição"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        variant="outlined"
                                        inputProps={{ maxLength: 255 }}
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                            </Box>

                            {/* CARD 2 — Itens de inspeção do grupo (drag-and-drop + config de respostas). */}
                            <Box sx={cardSx}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 800, mb: '3px' }}>Itens de inspeção do grupo</Typography>
                                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', maxWidth: 560, lineHeight: 1.5 }}>
                                        Arraste as linhas pela alça <Box component="b" sx={{ color: 'text.primary' }}>⠿</Box> para reordenar. Cada item define quais respostas ele aceita (<Box component="b" sx={{ color: 'text.primary' }}>OK / NOK / Manutenção</Box>) e, opcionalmente, uma resposta padrão pré-marcada no preenchimento.
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', mb: 2 }}>
                                    <Button variant="contained" size="small" startIcon={<Plus size={14} strokeWidth={2} />} onClick={addItem}>
                                        Adicionar item
                                    </Button>
                                    {editingItemId != null && (
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <Info size={14} strokeWidth={2} color={theme.palette.warning.main} />
                                            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                                                Conclua a edição do item em aberto para reordenar a lista.
                                            </Typography>
                                        </Stack>
                                    )}
                                </Box>

                                {items.length === 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: '36px 20px', color: 'text.disabled', textAlign: 'center', gap: '10px' }}>
                                        <Box sx={{ width: 56, height: 56, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Info size={22} strokeWidth={1.6} />
                                        </Box>
                                        <Typography sx={{ color: 'text.primary', fontSize: '13.5px', fontWeight: 700 }}>Nenhum item de inspeção neste grupo</Typography>
                                        <Typography sx={{ fontSize: '12.5px', maxWidth: 340 }}>
                                            Use &quot;Adicionar item&quot; acima para criar o primeiro item deste grupo.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Stack spacing={1.25}>
                                        {items.map((it, idx) => (
                                            it.insp_item_id === editingItemId ? (
                                                <ItemEditPanel
                                                    key={it.insp_item_id}
                                                    item={it}
                                                    index={idx}
                                                    onChangeText={(v) => handleChangeItemText(it.insp_item_id, v)}
                                                    onToggleOption={(k) => toggleOption(it.insp_item_id, k)}
                                                    onSetDefault={(k) => setItemDefault(it.insp_item_id, k)}
                                                    onCancel={() => cancelEditItem(it.insp_item_id)}
                                                    onCommit={() => commitEditItem(it.insp_item_id)}
                                                />
                                            ) : (
                                                <ItemRow
                                                    key={it.insp_item_id}
                                                    item={it}
                                                    index={idx}
                                                    dragLocked={editingItemId != null}
                                                    dragging={draggingId === it.insp_item_id}
                                                    onDragStart={(e) => handleRowDragStart(e, it.insp_item_id)}
                                                    onDragOver={(e) => handleRowDragOver(e, it.insp_item_id)}
                                                    onDragEnd={handleRowDragEnd}
                                                    onDuplicate={() => duplicateItem(it.insp_item_id)}
                                                    onEdit={() => startEditItem(it.insp_item_id)}
                                                    onRemove={() => removeItem(it.insp_item_id)}
                                                />
                                            )
                                        ))}
                                    </Stack>
                                )}

                                {/* groups-note — escopo deste editor (DESIGN-SYSTEM.md §5.15). */}
                                <Box sx={{
                                    display: 'flex', alignItems: 'flex-start', gap: '10px', mt: 2, p: '12px 14px',
                                    borderRadius: '12px', backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider',
                                }}>
                                    <Info size={16} strokeWidth={2} color={theme.palette.text.disabled} style={{ marginTop: 1, flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
                                        <Box component="span" sx={{ display: 'block', fontWeight: 700, color: 'text.primary', fontSize: '12.5px', mb: '2px' }}>
                                            Escopo deste editor
                                        </Box>
                                        Aqui você define quais itens existem no grupo, a ordem deles e quais respostas cada um aceita (e qual é a resposta padrão). O preenchimento de verdade — marcar OK/NOK/Manutenção item a item numa ordem de serviço específica — acontece em outra tela (preenchimento avulso de checklist / aba Checklist da operação da OS), fora do escopo deste editor.
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* PREVIEW STICKY — estrutura completa do grupo (check-group/check-item/check-answers, somente leitura). */}
                        <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                            <Typography sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', mb: 1.25 }}>
                                Pré-visualização ao vivo
                            </Typography>
                            <Box sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: `${theme.customRadius.hero - 2}px`,
                                backgroundColor: 'background.paper', boxShadow: theme.customShadows.md, overflow: 'hidden', mb: 1.75,
                            }}>
                                <Box sx={{ p: '16px 18px' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
                                        <span>Itens de inspeção</span>
                                        <Box component="b" sx={{ color: 'text.primary' }}>{items.length}</Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
                                        <span>Com resposta padrão</span>
                                        <Box component="b" sx={{ color: 'text.primary' }}>{withDefaultCount}</Box>
                                    </Box>
                                </Box>
                                <Box sx={{ height: '1px', bgcolor: 'divider', mx: '18px' }} />
                                <Box sx={{ p: '14px' }}>
                                    <PreviewCheckGroup name={groupNameForPreview} items={items} />
                                </Box>
                            </Box>
                            <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.5 }}>
                                É assim que o grupo aparece dentro da aba Checklist ao preencher uma ordem de serviço (mesmo componente da aba Checklist da Operação da OS) — os chips coloridos mostram só as respostas <Box component="b" sx={{ color: 'text.secondary' }}>habilitadas</Box> por item; a que estiver preenchida representa a resposta padrão pré-marcada. Isto é uma prévia estrutural, não um preenchimento real.
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Espaçador para compensar o rodapé fixo abaixo. */}
                <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                <Box sx={{
                    position: 'fixed', left: actionFooterLeft, right: 0, bottom: 0, zIndex: theme.zIndex.appBar,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
                    backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
                    boxShadow: '0 -6px 20px rgba(15,23,42,.06)', px: 3, py: 1.75, transition: 'left .2s ease',
                    pl: 'calc(24px + env(safe-area-inset-left, 0px))', pr: 'calc(24px + env(safe-area-inset-right, 0px))', pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                }}>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>Campos com * são obrigatórios</Typography>
                    <Stack direction="row" spacing={1.5}>
                        <Button variant="outlined" onClick={() => navigate(backTarget)} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving || loadingData}>
                            {saving ? 'Salvando...' : (isNew ? 'Salvar grupo' : 'Salvar alterações')}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </PageScaffold>
    );
}

// O antigo modo `embedded` (formulário embutido num sheet/drawer, usado só
// por `ListInspGroup.js`) foi removido: desde o redesign da lista de grupos
// reutilizáveis (FRONTEND-TASKS.md "Grupos de inspeção — lista reutilizável"),
// `ListInspGroup.js` navega para as rotas de página cheia deste componente
// (`/checklist/group/add`, `/checklist/group/edt?groupId=`) em vez de abrir
// um drawer — nenhuma outra tela do app consumia o modo `embedded`.
export default function GroupForm() {
    return <StandaloneGroupForm />;
}
